import React, { useState, useEffect } from 'react';
import { Button, Input, Typography, Table, Tag, Spin, Card, Collapse, Toast, Space, Tabs, Modal, TextArea, Progress } from '@douyinfe/semi-ui';
import { IconSearch, IconCopy, IconDownload, IconPlus, IconDelete, IconEdit } from '@douyinfe/semi-icons';
import { API, timestamp2string, copy } from '../helpers';
import { stringToColor } from '../helpers/render';
import { ITEMS_PER_PAGE } from '../constants';
import { renderModelPrice, renderQuota } from '../helpers/render';
import Paragraph from '@douyinfe/semi-ui/lib/es/typography/paragraph';
import { Tooltip } from '@douyinfe/semi-ui';
import Papa from 'papaparse';

const { Text } = Typography;
const { Panel } = Collapse;
const { TabPane } = Tabs;

function renderTimestamp(timestamp) {
    return timestamp2string(timestamp);
}

function renderIsStream(bool) {
    if (bool) {
        return <Tag color="blue" size="large">流</Tag>;
    } else {
        return <Tag color="purple" size="large">非流</Tag>;
    }
}

function renderUseTime(type) {
    const time = parseInt(type);
    if (time < 101) {
        return <Tag color="green" size="large"> {time} 秒 </Tag>;
    } else if (time < 300) {
        return <Tag color="orange" size="large"> {time} 秒 </Tag>;
    } else {
        return <Tag color="red" size="large"> {time} 秒 </Tag>;
    }
}

const LogsTable = () => {
    const [apikey, setAPIKey] = useState('');
    const [activeTabKey, setActiveTabKey] = useState('');
    const [tabData, setTabData] = useState({});
    const [loading, setLoading] = useState(false);
    const [activeKeys, setActiveKeys] = useState([]);
    const [pageSize, setPageSize] = useState(ITEMS_PER_PAGE);
    const [baseUrl, setBaseUrl] = useState('');
    const [sharedSites, setSharedSites] = useState([]);  // 公共站点数组（从API获取）
    const [sitesLoading, setSitesLoading] = useState(true);  // 站点加载状态
    const [hoveredTab, setHoveredTab] = useState(null);  // 悬停的Tab名称

    // 快速查询相关状态
    const [quickUrl, setQuickUrl] = useState('');
    const [quickKey, setQuickKey] = useState('');
    const [quickLoading, setQuickLoading] = useState(false);
    const [quickData, setQuickData] = useState(null);
    const [quickActiveKeys, setQuickActiveKeys] = useState([]);
    const [quickPageSize, setQuickPageSize] = useState(ITEMS_PER_PAGE);

    // 添加站点相关状态
    const [addSiteModalVisible, setAddSiteModalVisible] = useState(false);
    const [newSiteName, setNewSiteName] = useState('');
    const [newSiteUrl, setNewSiteUrl] = useState('');
    const [addingSite, setAddingSite] = useState(false);

    // 删除站点相关状态
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [deleteSiteName, setDeleteSiteName] = useState('');
    const [deletePassword, setDeletePassword] = useState('');
    const [deletingSite, setDeletingSite] = useState(false);

    // 编辑站点相关状态
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editSiteOldName, setEditSiteOldName] = useState('');
    const [editSiteNewName, setEditSiteNewName] = useState('');
    const [editSiteNewUrl, setEditSiteNewUrl] = useState('');
    const [editPassword, setEditPassword] = useState('');
    const [editingSite, setEditingSite] = useState(false);

    const PROXY_URL = 'https://proxy.cifang.xyz/proxy';

    // 构建 API URL（anyrouter.top 走代理）
    const buildApiUrl = (url) => {
        if (url.includes('anyrouter.top')) {
            return `${PROXY_URL}?url=${encodeURIComponent(url)}`;
        }
        return url;
    };

    // 批量查询相关状态
    const [batchModalVisible, setBatchModalVisible] = useState(false);
    const [batchKeys, setBatchKeys] = useState('');
    const [batchLoading, setBatchLoading] = useState(false);
    const [batchProgress, setBatchProgress] = useState(0);
    const [batchResults, setBatchResults] = useState([]);
    const [batchTotal, setBatchTotal] = useState(0);

    // 从 API 获取公共站点列表
    const fetchSharedSites = async () => {
        try {
            const response = await fetch('/api/sites');
            if (response.ok) {
                const sites = await response.json();
                setSharedSites(sites);
            }
        } catch (e) {
            console.error('获取站点列表失败:', e);
        } finally {
            setSitesLoading(false);
        }
    };

    useEffect(() => {
        fetchSharedSites();
    }, []);

    useEffect(() => {
        // 设置默认选中的站点
        if (sharedSites.length > 0 && !activeTabKey) {
            setActiveTabKey(sharedSites[0].name);
            setBaseUrl(sharedSites[0].url);
        }
    }, [sharedSites, activeTabKey]);

    const handleTabChange = (key) => {
        const site = sharedSites.find(s => s.name === key);
        setActiveTabKey(key);
        if (site) {
            setBaseUrl(site.url);
        }
    };

    // 添加新站点（调用API）
    const handleAddSite = async () => {
        const name = newSiteName.trim();
        const url = newSiteUrl.trim();

        if (!name) {
            Toast.warning('请输入站点名称');
            return;
        }
        if (!url) {
            Toast.warning('请输入站点 URL');
            return;
        }

        setAddingSite(true);
        try {
            const response = await fetch('/api/sites', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name, url })
            });
            const result = await response.json();

            if (response.ok) {
                setSharedSites(result.sites);
                setNewSiteName('');
                setNewSiteUrl('');
                setAddSiteModalVisible(false);
                Toast.success(`已添加站点: ${name}`);
                // 切换到新添加的站点
                const newSite = result.sites.find(s => s.name === name);
                setActiveTabKey(name);
                if (newSite) setBaseUrl(newSite.url);
            } else {
                Toast.error(result.error || '添加失败');
            }
        } catch (e) {
            Toast.error('添加失败: ' + e.message);
        } finally {
            setAddingSite(false);
        }
    };

    // 打开删除确认弹窗
    const openDeleteModal = (name) => {
        setDeleteSiteName(name);
        setDeletePassword('');
        setDeleteModalVisible(true);
    };

    // 删除站点（需要密码）
    const handleDeleteSite = async () => {
        if (!deletePassword) {
            Toast.warning('请输入删除密码');
            return;
        }

        setDeletingSite(true);
        try {
            const response = await fetch('/api/sites', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: deleteSiteName, password: deletePassword })
            });
            const result = await response.json();

            if (response.ok) {
                setSharedSites(result.sites);
                setDeleteModalVisible(false);
                Toast.success(`已删除站点: ${deleteSiteName}`);
                // 如果删除的是当前选中的站点，切换到第一个
                if (activeTabKey === deleteSiteName && result.sites.length > 0) {
                    setActiveTabKey(result.sites[0].name);
                    setBaseUrl(result.sites[0].url);
                }
            } else {
                Toast.error(result.error || '删除失败');
            }
        } catch (e) {
            Toast.error('删除失败: ' + e.message);
        } finally {
            setDeletingSite(false);
        }
    };

    // 打开编辑弹窗
    const openEditModal = (name, url) => {
        setEditSiteOldName(name);
        setEditSiteNewName(name);
        setEditSiteNewUrl(url);
        setEditPassword('');
        setEditModalVisible(true);
    };

    // 编辑站点（需要密码）
    const handleEditSite = async () => {
        if (!editPassword) {
            Toast.warning('请输入密码');
            return;
        }

        setEditingSite(true);
        try {
            const response = await fetch('/api/sites', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    oldName: editSiteOldName,
                    newName: editSiteNewName,
                    newUrl: editSiteNewUrl,
                    password: editPassword
                })
            });
            const result = await response.json();

            if (response.ok) {
                setSharedSites(result.sites);
                setEditModalVisible(false);
                Toast.success('站点已更新');
                // 如果名称改变了，更新当前选中
                const updatedSite = result.sites.find(s => s.name === editSiteNewName);
                if (activeTabKey === editSiteOldName && updatedSite) {
                    setActiveTabKey(editSiteNewName);
                    setBaseUrl(updatedSite.url);
                }
            } else {
                Toast.error(result.error || '编辑失败');
            }
        } catch (e) {
            Toast.error('编辑失败: ' + e.message);
        } finally {
            setEditingSite(false);
        }
    };

    const resetData = (key) => {
        setTabData((prevData) => ({
            ...prevData,
            [key]: {
                balance: 0,
                usage: 0,
                accessdate: "未知",
                logs: [],
                tokenValid: false,
            }
        }));
    };

    // 查询单个 Key 的余额（用于批量查询）
    const fetchSingleKeyBalance = async (key, url) => {
        try {
            const subscription = await API.get(buildApiUrl(`${url}/v1/dashboard/billing/subscription`), {
                headers: { Authorization: `Bearer ${key}` },
            });
            const subscriptionData = subscription.data;
            const balance = subscriptionData.hard_limit_usd ?? 0;

            let now = new Date();
            let start = new Date(now.getTime() - 100 * 24 * 3600 * 1000);
            let start_date = `${start.getFullYear()}-${start.getMonth() + 1}-${start.getDate()}`;
            let end_date = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
            const res = await API.get(buildApiUrl(`${url}/v1/dashboard/billing/usage?start_date=${start_date}&end_date=${end_date}`), {
                headers: { Authorization: `Bearer ${key}` },
            });
            const usage = (res.data.total_usage ?? 0) / 100;

            return { key, balance, usage, remaining: balance - usage, status: 'success' };
        } catch (e) {
            return { key, balance: 0, usage: 0, remaining: 0, status: 'error', error: e.message || '查询失败' };
        }
    };

    // 批量查询
    const handleBatchQuery = async () => {
        const keys = batchKeys
            .split('\n')
            .map(k => k.trim())
            .filter(k => k !== '');

        if (keys.length === 0) {
            Toast.warning('请输入至少一个 Key');
            return;
        }

        // 验证 Key 格式
        const validKeys = [];
        const invalidKeys = [];
        keys.forEach(k => {
            if (/^sk-[a-zA-Z0-9]{48}$/.test(k)) {
                validKeys.push(k);
            } else {
                invalidKeys.push(k);
            }
        });

        if (invalidKeys.length > 0) {
            Toast.warning(`${invalidKeys.length} 个 Key 格式不正确，已跳过`);
        }

        if (validKeys.length === 0) {
            Toast.error('没有有效的 Key');
            return;
        }

        setBatchLoading(true);
        setBatchProgress(0);
        setBatchResults([]);
        setBatchTotal(validKeys.length);

        const results = [];
        const concurrency = 5; // 并发数

        // 分批并发查询
        for (let i = 0; i < validKeys.length; i += concurrency) {
            const batch = validKeys.slice(i, i + concurrency);
            const batchResults = await Promise.all(
                batch.map(key => fetchSingleKeyBalance(key, baseUrl))
            );
            results.push(...batchResults);
            setBatchResults([...results]);
            setBatchProgress(Math.round((results.length / validKeys.length) * 100));
        }

        setBatchLoading(false);
        Toast.success(`查询完成，共 ${results.length} 个 Key`);
    };

    // 导出批量查询结果为 CSV
    const exportBatchCSV = () => {
        if (batchResults.length === 0) {
            Toast.warning('没有可导出的数据');
            return;
        }

        const csvData = batchResults.map((item, index) => ({
            '序号': index + 1,
            'Key': item.key.substring(0, 8) + '...' + item.key.substring(item.key.length - 4),
            '完整Key': item.key,
            '总额': item.balance === 100000000 ? '无限' : item.balance.toFixed(3),
            '已用': item.usage.toFixed(3),
            '剩余': item.balance === 100000000 ? '无限' : item.remaining.toFixed(3),
            '状态': item.status === 'success' ? '正常' : '失败',
            '备注': item.error || '',
        }));

        const csvString = '\ufeff' + Papa.unparse(csvData);
        const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `batch_keys_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // 批量查询结果表格列定义
    const batchColumns = [
        {
            title: '序号',
            dataIndex: 'index',
            width: 70,
            render: (_, __, index) => index + 1,
        },
        {
            title: 'Key',
            dataIndex: 'key',
            render: (text) => (
                <Tag color="grey" onClick={() => copyText(text)} style={{ cursor: 'pointer' }}>
                    {text.substring(0, 8)}...{text.substring(text.length - 4)}
                </Tag>
            ),
        },
        {
            title: '总额',
            dataIndex: 'balance',
            render: (text) => text === 100000000 ? '无限' : (text ?? 0).toFixed(3),
            sorter: (a, b) => a.balance - b.balance,
        },
        {
            title: '已用',
            dataIndex: 'usage',
            render: (text) => (text ?? 0).toFixed(3),
            sorter: (a, b) => a.usage - b.usage,
        },
        {
            title: '剩余',
            dataIndex: 'remaining',
            render: (text, record) => record.balance === 100000000 ? '无限' : (text ?? 0).toFixed(3),
            sorter: (a, b) => a.remaining - b.remaining,
        },
        {
            title: '状态',
            dataIndex: 'status',
            render: (text) => (
                <Tag color={text === 'success' ? 'green' : 'red'}>
                    {text === 'success' ? '正常' : '失败'}
                </Tag>
            ),
        },
    ];

    // 快速查询函数
    const fetchQuickData = async () => {
        if (quickUrl === '') {
            Toast.warning('请输入站点 URL');
            return;
        }
        if (quickKey === '') {
            Toast.warning('请输入令牌');
            return;
        }
        // 检查令牌格式
        if (!/^sk-[a-zA-Z0-9]{48}$/.test(quickKey)) {
            Toast.error('令牌格式非法！');
            return;
        }

        // 处理 URL，去掉末尾的斜杠
        let url = quickUrl.trim();
        if (url.endsWith('/')) {
            url = url.slice(0, -1);
        }

        setQuickLoading(true);
        try {
            let newData = { balance: 0, usage: 0, accessdate: 0, logs: [], tokenValid: false };

            if (process.env.REACT_APP_SHOW_BALANCE === "true") {
                const subscription = await API.get(buildApiUrl(`${url}/v1/dashboard/billing/subscription`), {
                    headers: { Authorization: `Bearer ${quickKey}` },
                });
                const subscriptionData = subscription.data;
                newData.balance = subscriptionData.hard_limit_usd ?? 0;
                newData.tokenValid = true;

                let now = new Date();
                let start = new Date(now.getTime() - 100 * 24 * 3600 * 1000);
                let start_date = `${start.getFullYear()}-${start.getMonth() + 1}-${start.getDate()}`;
                let end_date = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
                const res = await API.get(buildApiUrl(`${url}/v1/dashboard/billing/usage?start_date=${start_date}&end_date=${end_date}`), {
                    headers: { Authorization: `Bearer ${quickKey}` },
                });
                const data = res.data;
                newData.usage = (data.total_usage ?? 0) / 100;
            }

            if (process.env.REACT_APP_SHOW_DETAIL === "true") {
                const logRes = await API.get(buildApiUrl(`${url}/api/log/token?key=${quickKey}`), {
                    headers: { Authorization: `Bearer ${quickKey}` },
                });
                const { success, message, data: logData } = logRes.data;
                if (success) {
                    newData.logs = logData;
                    setQuickActiveKeys(['1', '2']);
                } else {
                    Toast.error('查询调用详情失败，请输入正确的令牌');
                }
            }

            setQuickData(newData);
            setQuickLoading(false);
        } catch (e) {
            Toast.error("查询失败，请检查 URL 和令牌是否正确");
            setQuickData(null);
            setQuickLoading(false);
            return;
        }
    };

    const fetchData = async () => {
        if (apikey === '') {
            Toast.warning('请先输入令牌，再进行查询');
            return;
        }
        // 检查令牌格式
        if (!/^sk-[a-zA-Z0-9]{48}$/.test(apikey)) {
            Toast.error('令牌格式非法！');
            return;
        }
        setLoading(true);
        try {
            let newTabData = { ...tabData[activeTabKey], balance: 0, usage: 0, accessdate: 0, logs: [], tokenValid: false };

            if (process.env.REACT_APP_SHOW_BALANCE === "true") {
                const subscription = await API.get(buildApiUrl(`${baseUrl}/v1/dashboard/billing/subscription`), {
                    headers: { Authorization: `Bearer ${apikey}` },
                });
                const subscriptionData = subscription.data;
                newTabData.balance = subscriptionData.hard_limit_usd ?? 0;
                newTabData.tokenValid = true;

                let now = new Date();
                let start = new Date(now.getTime() - 100 * 24 * 3600 * 1000);
                let start_date = `${start.getFullYear()}-${start.getMonth() + 1}-${start.getDate()}`;
                let end_date = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
                const res = await API.get(buildApiUrl(`${baseUrl}/v1/dashboard/billing/usage?start_date=${start_date}&end_date=${end_date}`), {
                    headers: { Authorization: `Bearer ${apikey}` },
                });
                const data = res.data;
                newTabData.usage = (data.total_usage ?? 0) / 100;
            }

            if (process.env.REACT_APP_SHOW_DETAIL === "true") {
                const logRes = await API.get(buildApiUrl(`${baseUrl}/api/log/token?key=${apikey}`), {
                    headers: { Authorization: `Bearer ${apikey}` },
                });
                const { success, message, data: logData } = logRes.data;
                if (success) {
                    newTabData.logs = logData;
                    let quota = 0;
                    for (let i = 0; i < logData.length; i++) {
                        quota += logData[i].quota;
                    }
                    setActiveKeys(['1', '2']); // 自动展开两个折叠面板
                } else {
                    Toast.error('查询调用详情失败，请输入正确的令牌');
                }
            }

            setTabData((prevData) => ({
                ...prevData,
                [activeTabKey]: newTabData,
            }));
            setLoading(false);
        } catch (e) {
            Toast.error("查询失败，请输入正确的令牌");
            resetData(activeTabKey); // 如果发生错误，重置所有数据为默认值
            setLoading(false);
            return;
        }
    };

    const copyText = async (text) => {
        if (await copy(text)) {
            Toast.success('已复制：' + text);
        } else {
            Modal.error({ title: '无法复制到剪贴板，请手动复制', content: text });
        }
    };

    const columns = [
        {
            title: '时间',
            dataIndex: 'created_at',
            render: renderTimestamp,
            sorter: (a, b) => a.created_at - b.created_at,
        },
        {
            title: '令牌名称',
            dataIndex: 'token_name',
            render: (text, record, index) => {
                return record.type === 0 || record.type === 2 ? (
                    <div>
                        <Tag
                            color="grey"
                            size="large"
                            onClick={() => {
                                copyText(text);
                            }}
                        >
                            {' '}
                            {text}{' '}
                        </Tag>
                    </div>
                ) : (
                    <></>
                );
            },
            sorter: (a, b) => ('' + a.token_name).localeCompare(b.token_name),
        },
        {
            title: '模型',
            dataIndex: 'model_name',
            render: (text, record, index) => {
                return record.type === 0 || record.type === 2 ? (
                    <div>
                        <Tag
                            color={stringToColor(text)}
                            size="large"
                            onClick={() => {
                                copyText(text);
                            }}
                        >
                            {' '}
                            {text}{' '}
                        </Tag>
                    </div>
                ) : (
                    <></>
                );
            },
            sorter: (a, b) => ('' + a.model_name).localeCompare(b.model_name),
        },
        {
            title: '用时',
            dataIndex: 'use_time',
            render: (text, record, index) => {
                return record.model_name.startsWith('mj_') ? null : (
                    <div>
                        <Space>
                            {renderUseTime(text)}
                            {renderIsStream(record.is_stream)}
                        </Space>
                    </div>
                );
            },
            sorter: (a, b) => a.use_time - b.use_time,
        },
        {
            title: '提示',
            dataIndex: 'prompt_tokens',
            render: (text, record, index) => {
                return record.model_name.startsWith('mj_') ? null : (
                    record.type === 0 || record.type === 2 ? <div>{<span> {text} </span>}</div> : <></>
                );
            },
            sorter: (a, b) => a.prompt_tokens - b.prompt_tokens,
        },
        {
            title: '补全',
            dataIndex: 'completion_tokens',
            render: (text, record, index) => {
                return parseInt(text) > 0 && (record.type === 0 || record.type === 2) ? (
                    <div>{<span> {text} </span>}</div>
                ) : (
                    <></>
                );
            },
            sorter: (a, b) => a.completion_tokens - b.completion_tokens,
        },
        {
            title: '花费',
            dataIndex: 'quota',
            render: (text, record, index) => {
                return record.type === 0 || record.type === 2 ? <div>{renderQuota(text, 6)}</div> : <></>;
            },
            sorter: (a, b) => a.quota - b.quota,
        },
        {
            title: '详情',
            dataIndex: 'content',
            render: (text, record, index) => {
                let other = null;
                try {
                    if (record.other === '') {
                        record.other = '{}';
                    }
                    other = JSON.parse(record.other);
                } catch (e) {
                    return (
                        <Tooltip content="该版本不支持显示计算详情">
                            <Paragraph
                                ellipsis={{
                                    rows: 2,
                                }}
                            >
                                {text}
                            </Paragraph>
                        </Tooltip>
                    );
                }
                if (other == null) {
                    return (
                        <Paragraph
                            ellipsis={{
                                rows: 2,
                                showTooltip: {
                                    type: 'popover',
                                },
                            }}
                        >
                            {text}
                        </Paragraph>
                    );
                }
                let content = renderModelPrice(
                    record.prompt_tokens,
                    record.completion_tokens,
                    other.model_ratio,
                    other.model_price,
                    other.completion_ratio,
                    other.group_ratio,
                );
                return (
                    <Tooltip content={content}>
                        <Paragraph
                            ellipsis={{
                                rows: 2,
                            }}
                        >
                            {text}
                        </Paragraph>
                    </Tooltip>
                );
            },
        }
    ];

    const copyTokenInfo = (e) => {
        e.stopPropagation();
        const activeTabData = tabData[activeTabKey] || {};
        const { balance, usage, accessdate } = activeTabData;
        const info = `令牌总额: ${balance === 100000000 ? '无限' : `${balance.toFixed(3)}`}
剩余额度: ${balance === 100000000 ? '无限制' : `${(balance - usage).toFixed(3)}`}
已用额度: ${balance === 100000000 ? '不进行计算' : `${usage.toFixed(3)}`}
有效期至: ${accessdate === 0 ? '永不过期' : renderTimestamp(accessdate)}`;
        copyText(info);
    };

    const exportCSV = (e) => {
        e.stopPropagation();
        const activeTabData = tabData[activeTabKey] || { logs: [] };
        const { logs } = activeTabData;
        const csvData = logs.map(log => ({
            '时间': renderTimestamp(log.created_at),
            '模型': log.model_name,
            '用时': log.use_time,
            '提示': log.prompt_tokens,
            '补全': log.completion_tokens,
            '花费': log.quota,
            '详情': log.content,
        }));
        const csvString = '\ufeff' + Papa.unparse(csvData);  // 使用PapaParse库来转换数据
        const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'data.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const copyQuickTokenInfo = (e) => {
        e.stopPropagation();
        if (!quickData) return;
        const { balance, usage, accessdate } = quickData;
        const info = `令牌总额: ${balance === 100000000 ? '无限' : `${balance.toFixed(3)}`}
剩余额度: ${balance === 100000000 ? '无限制' : `${(balance - usage).toFixed(3)}`}
已用额度: ${balance === 100000000 ? '不进行计算' : `${usage.toFixed(3)}`}
有效期至: ${accessdate === 0 ? '永不过期' : renderTimestamp(accessdate)}`;
        copyText(info);
    };

    const exportQuickCSV = (e) => {
        e.stopPropagation();
        if (!quickData || !quickData.logs) return;
        const { logs } = quickData;
        const csvData = logs.map(log => ({
            '时间': renderTimestamp(log.created_at),
            '模型': log.model_name,
            '用时': log.use_time,
            '提示': log.prompt_tokens,
            '补全': log.completion_tokens,
            '花费': log.quota,
            '详情': log.content,
        }));
        const csvString = '\ufeff' + Papa.unparse(csvData);
        const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'quick_query_data.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const activeTabData = tabData[activeTabKey] || { logs: [], balance: 0, usage: 0, accessdate: "未知", tokenValid: false };

    const renderContent = () => (
        <>
            <Card style={{ marginTop: 24 }}>
                <Input
                    showClear
                    value={apikey}
                    onChange={(value) => setAPIKey(value)}
                    placeholder="请输入要查询的令牌 sk-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    prefix={<IconSearch />}
                    suffix={
                        <Button
                            type='primary'
                            theme="solid"
                            onClick={fetchData}
                            loading={loading}
                            disabled={apikey === ''}
                        >
                            查询
                        </Button>
                    }
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            fetchData();
                        }
                    }}
                />
                <div style={{ marginTop: 12 }}>
                    <Button
                        icon={<IconPlus />}
                        onClick={() => setBatchModalVisible(true)}
                    >
                        批量查询
                    </Button>
                </div>
            </Card>

            {/* 批量查询 Modal */}
            <Modal
                title="批量查询 Key 余额"
                visible={batchModalVisible}
                onCancel={() => {
                    if (!batchLoading) {
                        setBatchModalVisible(false);
                    }
                }}
                footer={null}
                width={800}
                closable={!batchLoading}
                maskClosable={!batchLoading}
            >
                <div style={{ marginBottom: 16 }}>
                    <TextArea
                        placeholder="请粘贴要查询的 Key，每行一个&#10;sk-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx&#10;sk-yyyyyyyyyyyyyyyyyyyyyyyyyyyyyyyyyyyyyyyyyyyyyyyy"
                        value={batchKeys}
                        onChange={setBatchKeys}
                        rows={6}
                        disabled={batchLoading}
                    />
                </div>
                <div style={{ marginBottom: 16 }}>
                    <Space>
                        <Button
                            type="primary"
                            theme="solid"
                            onClick={handleBatchQuery}
                            loading={batchLoading}
                            disabled={batchKeys.trim() === ''}
                        >
                            开始查询
                        </Button>
                        {batchResults.length > 0 && (
                            <Button
                                icon={<IconDownload />}
                                onClick={exportBatchCSV}
                                disabled={batchLoading}
                            >
                                导出 CSV
                            </Button>
                        )}
                    </Space>
                </div>
                {batchLoading && (
                    <div style={{ marginBottom: 16 }}>
                        <Progress percent={batchProgress} showInfo />
                        <Text type="secondary" style={{ marginTop: 8, display: 'block' }}>
                            正在查询... {batchResults.length} / {batchTotal}
                        </Text>
                    </div>
                )}
                {batchResults.length > 0 && (
                    <div>
                        <div style={{ marginBottom: 8 }}>
                            <Text strong>查询结果：</Text>
                            <Text type="secondary" style={{ marginLeft: 8 }}>
                                共 {batchResults.length} 个，
                                成功 {batchResults.filter(r => r.status === 'success').length} 个，
                                失败 {batchResults.filter(r => r.status === 'error').length} 个
                            </Text>
                        </div>
                        <Table
                            columns={batchColumns}
                            dataSource={batchResults}
                            pagination={{
                                pageSize: 10,
                                showSizeChanger: true,
                                pageSizeOpts: [10, 20, 50, 100],
                                showTotal: (total, range) => `显示第 ${range[0]} 条-第 ${range[1]} 条，共 ${total} 条`,
                            }}
                            size="small"
                        />
                    </div>
                )}
            </Modal>

            <Card style={{ marginTop: 24 }}>
                <Collapse activeKey={activeKeys} onChange={(keys) => setActiveKeys(keys)}>
                    {process.env.REACT_APP_SHOW_BALANCE === "true" && (
                        <Panel
                            header="令牌信息"
                            itemKey="1"
                            extra={
                                <Button icon={<IconCopy />} theme='borderless' type='primary' onClick={(e) => copyTokenInfo(e)} disabled={!activeTabData.tokenValid}>
                                    复制令牌信息
                                </Button>
                            }
                        >
                            <Spin spinning={loading}>
                                <div style={{ marginBottom: 16 }}>
                                    <Text type="secondary">
                                        令牌总额：{activeTabData.balance === 100000000 ? "无限" : activeTabData.balance === "未知" || activeTabData.balance === undefined ? "未知" : `${activeTabData.balance.toFixed(3)}`}
                                    </Text>
                                    <br /><br />
                                    <Text type="secondary">
                                        剩余额度：{activeTabData.balance === 100000000 ? "无限制" : activeTabData.balance === "未知" || activeTabData.usage === "未知" || activeTabData.balance === undefined || activeTabData.usage === undefined ? "未知" : `${(activeTabData.balance - activeTabData.usage).toFixed(3)}`}
                                    </Text>
                                    <br /><br />
                                    <Text type="secondary">
                                        已用额度：{activeTabData.balance === 100000000 ? "不进行计算" : activeTabData.usage === "未知" || activeTabData.usage === undefined ? "未知" : `${activeTabData.usage.toFixed(3)}`}
                                    </Text>
                                    <br /><br />
                                    <Text type="secondary">
                                        有效期至：{activeTabData.accessdate === 0 ? '永不过期' : activeTabData.accessdate === "未知" ? '未知' : renderTimestamp(activeTabData.accessdate)}
                                    </Text>
                                </div>
                            </Spin>
                        </Panel>
                    )}
                    {process.env.REACT_APP_SHOW_DETAIL === "true" && (
                        <Panel
                            header="调用详情"
                            itemKey="2"
                            extra={
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <Tag shape='circle' color='green' style={{ marginRight: 5 }}>计算汇率：$1 = 50 0000 tokens</Tag>
                                    <Button icon={<IconDownload />} theme='borderless' type='primary' onClick={(e) => exportCSV(e)} disabled={!activeTabData.tokenValid || activeTabData.logs.length === 0}>
                                        导出为CSV文件
                                    </Button>
                                </div>
                            }
                        >
                            <Spin spinning={loading}>
                                <Table
                                    columns={columns}
                                    dataSource={activeTabData.logs}
                                    pagination={{
                                        pageSize: pageSize,
                                        hideOnSinglePage: true,
                                        showSizeChanger: true,
                                        pageSizeOpts: [10, 20, 50, 100],
                                        onPageSizeChange: (pageSize) => setPageSize(pageSize),
                                        showTotal: (total) => `共 ${total} 条`,
                                        showQuickJumper: true,
                                        total: activeTabData.logs.length,
                                        style: { marginTop: 12 },
                                    }}
                                />
                            </Spin>
                        </Panel>
                    )}
                </Collapse>
            </Card>
        </>
    );

    return (
        <>
            {/* 添加站点 Modal */}
            <Modal
                title="添加查询站点"
                visible={addSiteModalVisible}
                onCancel={() => !addingSite && setAddSiteModalVisible(false)}
                onOk={handleAddSite}
                okText="添加"
                cancelText="取消"
                confirmLoading={addingSite}
                closable={!addingSite}
                maskClosable={!addingSite}
            >
                <div style={{ marginBottom: 16 }}>
                    <Text>站点名称</Text>
                    <Input
                        placeholder="例如: cifang"
                        value={newSiteName}
                        onChange={setNewSiteName}
                        style={{ marginTop: 8 }}
                        disabled={addingSite}
                    />
                </div>
                <div>
                    <Text>站点 URL</Text>
                    <Input
                        placeholder="例如: https://cifang.xyz"
                        value={newSiteUrl}
                        onChange={setNewSiteUrl}
                        style={{ marginTop: 8 }}
                        disabled={addingSite}
                    />
                </div>
            </Modal>

            {/* 删除站点 Modal（需要密码） */}
            <Modal
                title={`删除站点: ${deleteSiteName}`}
                visible={deleteModalVisible}
                onCancel={() => !deletingSite && setDeleteModalVisible(false)}
                onOk={handleDeleteSite}
                okText="确认删除"
                cancelText="取消"
                okButtonProps={{ type: 'danger' }}
                confirmLoading={deletingSite}
                closable={!deletingSite}
                maskClosable={!deletingSite}
            >
                <div>
                    <Text>请输入删除密码</Text>
                    <Input
                        type="password"
                        placeholder="请输入密码"
                        value={deletePassword}
                        onChange={setDeletePassword}
                        style={{ marginTop: 8 }}
                        disabled={deletingSite}
                    />
                </div>
            </Modal>

            {/* 编辑站点 Modal（需要密码） */}
            <Modal
                title={`编辑站点: ${editSiteOldName}`}
                visible={editModalVisible}
                onCancel={() => !editingSite && setEditModalVisible(false)}
                onOk={handleEditSite}
                okText="确认修改"
                cancelText="取消"
                confirmLoading={editingSite}
                closable={!editingSite}
                maskClosable={!editingSite}
            >
                <div style={{ marginBottom: 16 }}>
                    <Text>站点名称</Text>
                    <Input
                        placeholder="站点名称"
                        value={editSiteNewName}
                        onChange={setEditSiteNewName}
                        style={{ marginTop: 8 }}
                        disabled={editingSite}
                    />
                </div>
                <div style={{ marginBottom: 16 }}>
                    <Text>站点 URL</Text>
                    <Input
                        placeholder="站点 URL"
                        value={editSiteNewUrl}
                        onChange={setEditSiteNewUrl}
                        style={{ marginTop: 8 }}
                        disabled={editingSite}
                    />
                </div>
                <div>
                    <Text>验证密码</Text>
                    <Input
                        type="password"
                        placeholder="请输入密码"
                        value={editPassword}
                        onChange={setEditPassword}
                        style={{ marginTop: 8 }}
                        disabled={editingSite}
                    />
                </div>
            </Modal>

            {/* 快速查询区域 */}
            <Card title="快速查询" style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div style={{ flex: '1 1 300px', minWidth: 200 }}>
                        <Text size="small" style={{ marginBottom: 4, display: 'block' }}>站点 URL</Text>
                        <Input
                            placeholder="例如: https://api.example.com"
                            value={quickUrl}
                            onChange={setQuickUrl}
                            showClear
                        />
                    </div>
                    <div style={{ flex: '1 1 400px', minWidth: 300 }}>
                        <Text size="small" style={{ marginBottom: 4, display: 'block' }}>API Key</Text>
                        <Input
                            placeholder="sk-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                            value={quickKey}
                            onChange={setQuickKey}
                            showClear
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    fetchQuickData();
                                }
                            }}
                        />
                    </div>
                    <Button
                        type="primary"
                        theme="solid"
                        icon={<IconSearch />}
                        onClick={fetchQuickData}
                        loading={quickLoading}
                        disabled={quickUrl === '' || quickKey === ''}
                    >
                        查询
                    </Button>
                </div>

                {/* 快速查询结果展示 */}
                {quickData && (
                    <div style={{ marginTop: 16 }}>
                        <Collapse activeKey={quickActiveKeys} onChange={(keys) => setQuickActiveKeys(keys)}>
                            {process.env.REACT_APP_SHOW_BALANCE === "true" && (
                                <Panel
                                    header="令牌信息"
                                    itemKey="1"
                                    extra={
                                        <Button icon={<IconCopy />} theme='borderless' type='primary' onClick={(e) => copyQuickTokenInfo(e)} disabled={!quickData.tokenValid}>
                                            复制令牌信息
                                        </Button>
                                    }
                                >
                                    <Spin spinning={quickLoading}>
                                        <div style={{ marginBottom: 16 }}>
                                            <Text type="secondary">
                                                令牌总额：{quickData.balance === 100000000 ? "无限" : quickData.balance === "未知" || quickData.balance === undefined ? "未知" : `${quickData.balance.toFixed(3)}`}
                                            </Text>
                                            <br /><br />
                                            <Text type="secondary">
                                                剩余额度：{quickData.balance === 100000000 ? "无限制" : quickData.balance === "未知" || quickData.usage === "未知" || quickData.balance === undefined || quickData.usage === undefined ? "未知" : `${(quickData.balance - quickData.usage).toFixed(3)}`}
                                            </Text>
                                            <br /><br />
                                            <Text type="secondary">
                                                已用额度：{quickData.balance === 100000000 ? "不进行计算" : quickData.usage === "未知" || quickData.usage === undefined ? "未知" : `${quickData.usage.toFixed(3)}`}
                                            </Text>
                                            <br /><br />
                                            <Text type="secondary">
                                                有效期至：{quickData.accessdate === 0 ? '永不过期' : quickData.accessdate === "未知" ? '未知' : renderTimestamp(quickData.accessdate)}
                                            </Text>
                                        </div>
                                    </Spin>
                                </Panel>
                            )}
                            {process.env.REACT_APP_SHOW_DETAIL === "true" && (
                                <Panel
                                    header="调用详情"
                                    itemKey="2"
                                    extra={
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <Tag shape='circle' color='green' style={{ marginRight: 5 }}>计算汇率：$1 = 50 0000 tokens</Tag>
                                            <Button icon={<IconDownload />} theme='borderless' type='primary' onClick={(e) => exportQuickCSV(e)} disabled={!quickData.tokenValid || quickData.logs.length === 0}>
                                                导出为CSV文件
                                            </Button>
                                        </div>
                                    }
                                >
                                    <Spin spinning={quickLoading}>
                                        <Table
                                            columns={columns}
                                            dataSource={quickData.logs}
                                            pagination={{
                                                pageSize: quickPageSize,
                                                hideOnSinglePage: true,
                                                showSizeChanger: true,
                                                pageSizeOpts: [10, 20, 50, 100],
                                                onPageSizeChange: (size) => setQuickPageSize(size),
                                                showTotal: (total) => `共 ${total} 条`,
                                                showQuickJumper: true,
                                                total: quickData.logs.length,
                                                style: { marginTop: 12 },
                                            }}
                                        />
                                    </Spin>
                                </Panel>
                            )}
                        </Collapse>
                    </div>
                )}
            </Card>

            <div style={{ marginBottom: 16 }}>
                <Button icon={<IconPlus />} onClick={() => setAddSiteModalVisible(true)}>
                    添加站点
                </Button>
            </div>

            {sitesLoading ? (
                <Card>
                    <Spin tip="加载站点列表...">
                        <div style={{ height: 100 }}></div>
                    </Spin>
                </Card>
            ) : sharedSites.length > 0 ? (
                <>
                    <Tabs type="card" activeKey={activeTabKey} onChange={handleTabChange}>
                        {sharedSites.map((site) => (
                            <TabPane
                                tab={
                                    <Tooltip content={site.url}>
                                        <span
                                            onMouseEnter={() => setHoveredTab(site.name)}
                                            onMouseLeave={() => setHoveredTab(null)}
                                            style={{ display: 'inline-flex', alignItems: 'center' }}
                                        >
                                            {site.name}
                                            {hoveredTab === site.name && (
                                                <>
                                                    <IconEdit
                                                        style={{ marginLeft: 8, color: '#999', cursor: 'pointer' }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            openEditModal(site.name, site.url);
                                                        }}
                                                    />
                                                    <IconDelete
                                                        style={{ marginLeft: 4, color: '#999', cursor: 'pointer' }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            openDeleteModal(site.name);
                                                        }}
                                                    />
                                                </>
                                            )}
                                        </span>
                                    </Tooltip>
                                }
                                itemKey={site.name}
                                key={site.name}
                            >
                                {renderContent()}
                            </TabPane>
                        ))}
                    </Tabs>
                    <div style={{ marginTop: -12, marginBottom: 12, padding: '8px 12px', background: 'var(--semi-color-fill-0)', borderRadius: 4 }}>
                        <Text type="tertiary" size="small">当前站点: </Text>
                        <Text size="small" copyable>{baseUrl}</Text>
                    </div>
                </>
            ) : (
                <Card>
                    <Text type="secondary">暂无站点，请点击上方"添加站点"按钮添加</Text>
                </Card>
            )}
        </>
    );
};

export default LogsTable;
